import {
    listMesas,
    listOcupacion,
    assignMesa,
    unassignMesa,
    upsertMesas,
} from '../infrastructure/mesas.repository.js';
import { fetchAreas } from '../infrastructure/reservations.repository.js';
import { FudoGpRepository } from '../infrastructure/fudo-gp.repository.js';
import {
    buildFloorPlan,
    mapFudoFloorPlan,
    reconcileAsignacion,
    ESTADO_MESA,
} from '../shared/mesas-policy.js';

/**
 * Servicio de asignación de mesas para el panel de staff. Orquesta los
 * repositorios (I/O) y la política pura (`mesas-policy.js`); no arma view
 * models a mano ni conoce el SDK de Supabase.
 *
 * Los métodos de escritura NO lanzan: devuelven `{ ok, message? }`. El
 * llamador (admin.js) muestra el mensaje en un Toast, igual que el resto del
 * panel. Lanzar obligaría a cada call site a repetir el try/catch.
 */

/** Código de Postgres para unique_violation — el bloqueo de doble booking. */
const UNIQUE_VIOLATION = '23505';

const MESA_TOMADA_SIN_DUENIO = 'Esa mesa ya está tomada en este turno.';
const ASIGNAR_ERROR_MSG = 'No pudimos asignar la mesa. Probá de nuevo.';
const LIBERAR_ERROR_MSG = 'No pudimos liberar la mesa. Probá de nuevo.';
const SYNC_ERROR_MSG = 'No pudimos sincronizar las mesas con Fudo. Probá de nuevo.';

/**
 * Plano de mesas del área de la reserva, con el estado de cada mesa para el
 * turno de esa reserva.
 *
 * @param {{ reservation: { id: string, fecha: string, momento: string, area: string } }} params
 * @returns {Promise<{ plan: object, asignadas: Array<object> }>}
 * @throws {Error} si falla la lectura (la vista muestra su estado de error)
 */
export async function loadFloorPlan({ reservation }) {
    const [mesas, ocupacion] = await Promise.all([
        listMesas({ area: reservation.area }),
        listOcupacion({ fecha: reservation.fecha, momento: reservation.momento }),
    ]);

    const plan = buildFloorPlan({ mesas, ocupacion, reservationId: reservation.id });
    const asignadas = plan.mesas.filter((mesa) => mesa.estado === ESTADO_MESA.ASIGNADA);

    return { plan, asignadas };
}

/**
 * Nombre de quien tiene tomada una mesa en el turno de la reserva, leído
 * FRESCO. En un conflicto el plano en pantalla es justamente el que estaba
 * desactualizado — es la razón por la que el conflicto ocurrió — así que
 * reusarlo daría el nombre equivocado o ninguno.
 *
 * @param {{ reservation: object, mesaId: number }} params
 * @returns {Promise<string|null>}
 */
async function quienOcupa({ reservation, mesaId }) {
    try {
        const ocupacion = await listOcupacion({
            fecha: reservation.fecha,
            momento: reservation.momento,
        });
        return ocupacion.find((row) => row.mesa_id === mesaId)?.label || null;
    } catch {
        // El nombre es un lujo; el bloqueo ya ocurrió y hay que reportarlo igual.
        return null;
    }
}

/**
 * Asigna una mesa a la reserva. El bloqueo de doble booking lo hace la base
 * (índice `reservation_tables_no_doble_booking`), no este módulo: acá solo se
 * traduce el `23505` a algo que el staff pueda leer y accionar.
 *
 * @param {{ reservation: object, mesaId: number }} params
 * @returns {Promise<{ ok: boolean, message?: string }>}
 */
export async function asignarMesa({ reservation, mesaId }) {
    try {
        await assignMesa({ reservationId: reservation.id, mesaId });
        return { ok: true };
    } catch (error) {
        if (error?.code !== UNIQUE_VIOLATION) {
            return { ok: false, message: ASIGNAR_ERROR_MSG };
        }

        const duenio = await quienOcupa({ reservation, mesaId });
        return {
            ok: false,
            message: duenio
                ? `Esa mesa ya está tomada en este turno por ${duenio}.`
                : MESA_TOMADA_SIN_DUENIO,
        };
    }
}

/**
 * Libera una mesa de la reserva.
 * @param {{ reservation: object, mesaId: number }} params
 * @returns {Promise<{ ok: boolean, message?: string }>}
 */
export async function liberarMesa({ reservation, mesaId }) {
    try {
        await unassignMesa({ reservationId: reservation.id, mesaId });
        return { ok: true };
    } catch {
        return { ok: false, message: LIBERAR_ERROR_MSG };
    }
}

/**
 * Aplica la selección de mesas del formulario a una reserva, reconciliando
 * contra lo que ya tenía: asigna lo agregado, libera lo quitado, no toca el
 * resto.
 *
 * Devuelve los conflictos en vez de abortar en el primero: si el operador
 * eligió cuatro mesas y una se la ganaron hace tres segundos, las otras tres
 * se asignan igual y se reporta cuál falló. Abortar en la primera dejaría la
 * reserva a medio asignar sin decir dónde quedó.
 *
 * @param {{ reservation: object, actuales: Array<{id:number}>, seleccion: Iterable<number> }} params
 * @returns {Promise<{ ok: boolean, conflictos: Array<{ mesaId: number, message: string }> }>}
 */
export async function guardarAsignacion({ reservation, actuales, seleccion }) {
    const { aAsignar, aLiberar } = reconcileAsignacion(actuales, seleccion);
    const conflictos = [];

    for (const mesaId of aLiberar) {
        const result = await liberarMesa({ reservation, mesaId });
        if (!result.ok) conflictos.push({ mesaId, message: result.message });
    }

    for (const mesaId of aAsignar) {
        const result = await asignarMesa({ reservation, mesaId });
        if (!result.ok) conflictos.push({ mesaId, message: result.message });
    }

    return { ok: conflictos.length === 0, conflictos };
}

/**
 * Sincroniza el plano de mesas desde Fudo (dueño del layout físico) hacia
 * `mesas`. Idempotente: la PK es el id de Fudo, así que correrlo dos veces no
 * duplica nada.
 *
 * `unmapped` son mesas cuyo salón de Fudo no tiene área local equivalente
 * (por ejemplo un salón nuevo). Se devuelven para avisar, NO se descartan en
 * silencio: un salón renombrado en Fudo se vería como un piso vacío.
 *
 * @returns {Promise<{ ok: boolean, total?: number, unmapped?: Array<object>, message?: string }>}
 */
export async function sincronizarMesas() {
    try {
        const [areasMap, roomsRes, tablesRes] = await Promise.all([
            fetchAreas(),
            FudoGpRepository.get('/rooms'),
            FudoGpRepository.get('/tables'),
        ]);

        const { mesas, unmapped } = mapFudoFloorPlan({
            rooms: roomsRes.data ?? [],
            tables: tablesRes.data ?? [],
            areaNames: Object.keys(areasMap),
        });

        await upsertMesas(mesas);
        return { ok: true, total: mesas.length, unmapped };
    } catch {
        return { ok: false, message: SYNC_ERROR_MSG };
    }
}
